import React, { useRef, useState, useEffect } from "react";
import { GoogleMap, Marker, InfoWindow, useLoadScript } from "@react-google-maps/api";
import { useTheme } from '../../contexts/ThemeContext';
import Footer from "./Footer";

import { CITIES_DATA } from "../../data/citiesData";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const containerStyle = { width: "100%", height: "100%" };
const initialCenter = { lat: -21.192599, lng: -47.8098659 };
const initialZoom = 11;

const isMobile = window.innerWidth <= 768;
const LAT_OFFSET = isMobile ? -0.30 : -0.17;
const mapCenterOffset = { lat: initialCenter.lat + LAT_OFFSET, lng: initialCenter.lng };

// transforma objeto em array
const citiesArray = Object.values(CITIES_DATA);

const MapContent = () => {
  const { theme } = useTheme();
  const [selectedCity, setSelectedCity] = useState(citiesArray[0]);
  const [mapCenter, setMapCenter] = useState(mapCenterOffset);
  const mapRef = useRef(null);

  // Filtra cidades de acordo com o contexto
  const CitiesFilter = citiesArray.filter(c => {
    const ctx = theme?.context?.toLowerCase() || "";
    if (ctx === "") return true;
    if (ctx.includes("respirat")) return c.cpap === true;
    if (ctx.includes("home")) return c.home === true;
    if (ctx.includes("audit")) return c.aud === true;
    return false;
  });

  useEffect(() => {
    if (CitiesFilter.length > 0) {
      const city = CitiesFilter[0];
      setSelectedCity(city);
      setMapCenter({ lat: city.lat + LAT_OFFSET, lng: city.lng });
    }
  }, [theme.context]);

  const handleMapLoad = (map) => { mapRef.current = map; };

  const handleCityClick = (city, keepZoom = true, newZoom = 14) => {
    if (!mapRef.current) return;
    setSelectedCity(city);
    const target = { lat: city.lat + LAT_OFFSET, lng: city.lng };
    setMapCenter(target);
    mapRef.current.panTo(target);
    if (!keepZoom) mapRef.current.setZoom(newZoom);
  };

  const cardWidth = 500; // largura aproximada (card + margin)
  const speed = 60;     // px/segundo
  const totalWidth = CitiesFilter.length * cardWidth;
  const duration = totalWidth / speed; // segundos

  return (
    <div className="relative w-full h-[2000px] md:h-[1350px] overflow-hidden mt-12">
      {/* MAPA GOOGLE */}
      <div className="absolute inset-0 z-0">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={initialZoom}
          onLoad={handleMapLoad}
          options={{
            disableDefaultUI: true,
            draggable: false,
            zoomControl: false,
            scrollwheel: false,
            disableDoubleClickZoom: true,
            gestureHandling: "none",
            mapTypeId: "roadmap",
            styles: [
              { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#d5ead5" }] },
              { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#b5dcb5" }] },
              { featureType: "water", elementType: "geometry", stylers: [{ color: "#8fc8e3" }] },
              { featureType: "road", elementType: "geometry", stylers: [{ color: "#e8e8e8" }] },
              { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#333333" }] },
              { featureType: "all", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
            ],
          }}
        >
          {selectedCity && (
            <Marker position={{ lat: selectedCity.lat, lng: selectedCity.lng }} animation={window.google?.maps?.Animation?.DROP}>
              <InfoWindow
                position={{ lat: selectedCity.lat, lng: selectedCity.lng }}
              >
                <div className="text-gray-800 text-sm">
                  <hr />
                  <p className="font-semibold text-base mt-3">{selectedCity.name}</p>
                  <p className="font-semibold text-base mb-1 text-gray-600">{selectedCity.phone}</p>
                  {selectedCity.address && <p>{selectedCity.address}</p>}
                  {selectedCity.neighborhood && (
                    <p>
                      {selectedCity.neighborhood} - {selectedCity.cidade} / {selectedCity.state}
                    </p>
                  )}
                  {selectedCity.cep && <p>CEP: {selectedCity.cep}</p>}
                  <p className="text-gray-500">{selectedCity.hours}</p>
                  <button className={`flex items-center font-medium py-1 px-3 mt-2 rounded-lg shadow-md transition-colors ${theme.buttonBg} ${theme.buttonHoverBg}`}>
                    <a className="font-medium text-white" href={`/city/${selectedCity.id}`}>Ver Mais..</a>
                  </button>
                </div>
              </InfoWindow>
            </Marker>
          )}
        </GoogleMap>
      </div>

      {/* CONTROLES E SOBREPOSIÇÕES */}
      <div className={`absolute inset-0 z-[5] pointer-events-none bg-gradient-to-b from-[#f3f6f8] via-white/20 to-transparent h-[20%]`}></div>

      <div className="absolute inset-0 z-10 flex flex-col justify-between items-center pointer-events-none">
        {/* CARD DE AJUDA */}
        <div className="w-full max-w-5xl bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-3 flex items-center justify-between pointer-events-auto mt-4 mx-4">
          <div className="pl-8">
            <p className="text text-gray-900">Precisa de Ajuda?</p>
            <p className="text-sm text-gray-400">Formulário • Contato</p>
            <p className="text-sm text-gray-400">08:00 • 18:00</p>
          </div>
          <div className="d-flex flex-row">
            <p className="hidden md:block text-sm text-gray-400 max-w-md px-2">
              Não consegue encontrar exatamente o que procura?
            </p>
            <p className="hidden md:block text-sm text-gray-400 max-w-md px-2">
              Entre em contato com os nossos atendentes treinados para te guiar
              na sua escolha para o melhor do seu tratamento.
            </p>
          </div>
          <a className="bg-black text-white font-semibold text-sm mr-8 px-6 py-3 rounded-full hover:bg-gray-800 transition-colors whitespace-nowrap pointer-events-auto"
            href={`https://wa.me/${selectedCity.phone}?text=${encodeURIComponent("Olá! Gostaria de falar com um especialista."
            )}`}>
            Entrar em contato
          </a>
        </div>

        {/* CARROSSEL DE CIDADES */}
        <div className="w-full flex flex-col items-center mt-6 pointer-events-auto">
          {(() => {
            const citiesCount = CitiesFilter.length;

            // duração baseada na quantidade de cidades
            const duration =
              citiesCount <= 1
                ? 0
                : citiesCount <= 3
                  ? 45
                  : 25;

            // só duplica se tiver mais de 1 cidade
            const citiesToRender =
              citiesCount > 1
                ? [...CitiesFilter, ...CitiesFilter]
                : CitiesFilter;

            return (
              <>
                <div className="w-full overflow-hidden">
                  <div
                    className={`flex gap-4 ${citiesCount > 1 ? "animate-slide-Map" : ""
                      }`}
                    style={
                      citiesCount > 1
                        ? { animationDuration: `${duration}s` }
                        : {}
                    }
                  >
                    {citiesToRender.map((city) => (
                      <div
                        key={city.id}
                        onClick={() => handleCityClick(city, true)}
                        className={`group relative flex-shrink-0 w-[460px] bg-white/65 backdrop-blur-md rounded-2xl p-1 m-2 shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer 
                ${selectedCity.name === city.name
                            ? "border-4"
                            : "border-4 border-transparent"
                          }`}
                      >
                        <div className="flex flex-row">
                          <div className="flex flex-col justify-between h-full w-[85%]">
                            <div className="flex items-center justify-center h-28 w-full bg-white rounded-tl-lg rounded-tr-none mb-2">
                              <img
                                src={city.img}
                                alt={`Foto de ${city.name}`}
                                className="w-full h-full object-cover rounded-l-xl"
                              />
                            </div>

                            <div className="mb-0 p-2">
                              <p className="font-bold text-gray-800 text-base mb-1">
                                {(city.cpap && theme.context.includes("respirat")
                                  ? "Equipamentos Respiratórios "
                                  : city.aud && theme.context.includes("audit")
                                    ? "Equipamentos Auditivos "
                                    : city.home &&
                                      (theme.context.includes("home") ||
                                        theme.context === "")
                                      ? "Home Care "
                                      : "") + city.name}
                              </p>

                              <p className="text-sm text-gray-600 mb-2">
                                {city.hours}
                              </p>

                              <div className="flex flex-wrap gap-2">
                                {city.cpap &&
                                  (theme.context.includes("respirat") ||
                                    theme.context === "") && (
                                    <span className="bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                                      Equipamentos Respiratórios
                                    </span>
                                  )}

                                {city.aud &&
                                  (theme.context.includes("audit") ||
                                    theme.context === "") && (
                                    <span className="bg-purple-400 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                                      Aparelhos Auditivos
                                    </span>
                                  )}

                                {city.home &&
                                  (theme.context.includes("home") ||
                                    theme.context === "") && (
                                    <span className="bg-blue-400 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                                      Home Care
                                    </span>
                                  )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center w-[15%]">
                            <div className="flex items-center border-l border-gray-400 h-[90%]">
                              <svg
                                width="25"
                                height="29"
                                viewBox="0 0 30 34"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M11.0242 5.35522L22.0889 16.6295L11.0242 27.9035"
                                  stroke="#A7A7A7"
                                  strokeWidth="2"
                                  strokeMiterlimit="10"
                                />
                                <path
                                  d="M7.33594 24.8353L7.33594 8.42334"
                                  stroke="#A7A7A7"
                                  strokeWidth="2"
                                  strokeMiterlimit="10"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* PROGRESS BAR SINCRONIZADA */}
                {citiesCount > 1 && (
                  <div className="w-[87vw] flex items-center max-w-none h-6 bg-white/80 rounded-full border-4 mt-6 overflow-hidden border border-white">
                    <div
                      className="ml-2 h-[60%] rounded-full animate-progress-bar"
                      style={{
                        backgroundColor: theme.progressBar,
                        borderLeftColor: theme.progressBar,
                        borderLeftWidth: "6px",
                        animationDuration: `${duration}s`,
                      }}
                    ></div>
                  </div>
                )}

                <div className="w-full !mt-0">
                  <Footer />
                </div>
              </>
            );
          })()}
        </div>
      </div>

      <div className={`absolute inset-x-0 bottom-0 z-[5] pointer-events-none to-transparent h-[1600px]`}
        style={{ backgroundImage: `linear-gradient(to top, ${theme.gradientColor}, rgba(255, 255, 255, 0.3), transparent)` }}></div>
    </div>
  );
};

const MapLoader = ({ children }) => {
  const { isLoaded, loadError } = useLoadScript({ googleMapsApiKey: API_KEY });

  if (loadError) return <div className="text-red-600 text-center p-8">Erro ao carregar o mapa.</div>;
  if (!isLoaded) return <div className="text-gray-500 text-center p-8">Carregando Mapa...</div>;
  return <>{children}</>;
};

const MapAndCards = (props) => (
  <MapLoader>
    <MapContent {...props} />
  </MapLoader>
);

export default MapAndCards;
